import * as React from 'react';
import { Settings, Filter, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@components/base/actions';
import { Card, CardHeader, CardContent, Panel, VStack, HStack, Grid } from '@components/base/layout';
import { Icon, Text } from '@components/base/display';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

const variants = ['default', 'elevated', 'outlined', 'ghost'] as const;
const paddings = ['none', 'compact', 'default', 'comfortable'] as const;

function PanelShowcaseComponent({ playgroundProps, isDarkMode: _isDarkMode }: Readonly<ShowcaseProps>) {
  const [expanded1, setExpanded1] = React.useState(true);
  const [expanded2, setExpanded2] = React.useState(false);

  return (
    <VStack gap={6}>
      {/* Interactive Preview */}
      <ShowcasePreviewSection componentName="panel">
          <Panel
            variant={playgroundProps.variant as 'default' | 'elevated' | 'outlined' | 'ghost'}
            padding={playgroundProps.padding as 'none' | 'compact' | 'default' | 'comfortable'}
            className="max-w-md"
          >
            <Text size="lg" weight="semibold">Panel Content</Text>
            <Text size="sm" color="muted" className="mt-2">
              A simple container for grouping related content without the structured header/footer of a Card.
            </Text>
          </Panel>
      </ShowcasePreviewSection>

      {/* Variants */}
      <ShowcaseDemoSection
        title="Variants"
        description="Four visual styles: default, elevated, outlined, and ghost"
       
      >
          <Grid columns={{ base: 1, md: 2 }} gap={4}>
            {variants.map((variant) => (
              <Panel key={variant} variant={variant}>
                <Text size="sm" weight="semibold" className="capitalize">{variant}</Text>
                <Text size="xs" color="muted" className="mt-1">
                  {variant === 'default' && 'Shadow and border'}
                  {variant === 'elevated' && 'Shadow only, no border'}
                  {variant === 'outlined' && 'Border only, no shadow'}
                  {variant === 'ghost' && 'No styling, transparent'}
                </Text>
              </Panel>
            ))}
          </Grid>
      </ShowcaseDemoSection>

      {/* Padding Options */}
      <ShowcaseDemoSection
        title="Padding Options"
        description="Density-based padding: none, compact (12px), default (20px), comfortable (28px)"
       
      >
          <VStack gap={4}>
            {paddings.map((padding) => (
              <Panel key={padding} padding={padding} className="max-w-md">
                <Text size="sm" weight="semibold" className="capitalize">{padding}</Text>
                <Text size="xs" color="muted">
                  {padding === 'none' && 'No padding'}
                  {padding === 'compact' && '12px - for data-heavy screens'}
                  {padding === 'default' && '20px - standard interfaces'}
                  {padding === 'comfortable' && '28px - client-facing'}
                </Text>
              </Panel>
            ))}
          </VStack>
      </ShowcaseDemoSection>

      {/* Basic Panel */}
      <ShowcaseDemoSection
        title="Basic Panel"
        description="Simple content containers without structured sections"
       
      >
          <VStack gap={4} className="max-w-md">
            <Panel>
              <Text size="sm">
                A Panel is a simpler alternative to Card when you don&apos;t need
                header/footer structure. It&apos;s just a styled container.
              </Text>
            </Panel>
            <Panel variant="outlined">
              <VStack gap={2}>
                <Text size="sm" weight="semibold">Quick Tip</Text>
                <Text size="sm" color="muted">
                  Use Panels for sidebar filters, quick stats, or any content
                  that needs visual grouping without header complexity.
                </Text>
              </VStack>
            </Panel>
          </VStack>
      </ShowcaseDemoSection>

      {/* Collapsible Panels */}
      <ShowcaseDemoSection
        title="Collapsible Panels"
        description="Panels with custom expand/collapse behavior"
       
      >
          <VStack gap={4} className="max-w-md">
            <Panel padding="none">
              <button
                type="button"
                className="w-full flex items-center justify-between p-4 cursor-pointer"
                onClick={() => { setExpanded1(!expanded1); }}
                aria-expanded={expanded1}
              >
                <Text size="sm" weight="semibold">Coverage Details</Text>
                <Icon icon={expanded1 ? ChevronUp : ChevronDown} size="sm" />
              </button>
              {expanded1 && (
                <div className="px-4 pb-4">
                  <VStack gap={2}>
                    <HStack justify="between">
                      <Text size="sm" color="muted">Liability</Text>
                      <Text size="sm">$100,000</Text>
                    </HStack>
                    <HStack justify="between">
                      <Text size="sm" color="muted">Collision</Text>
                      <Text size="sm">$50,000</Text>
                    </HStack>
                    <HStack justify="between">
                      <Text size="sm" color="muted">Comprehensive</Text>
                      <Text size="sm">$50,000</Text>
                    </HStack>
                  </VStack>
                </div>
              )}
            </Panel>

            <Panel padding="none">
              <button
                type="button"
                className="w-full flex items-center justify-between p-4 cursor-pointer"
                onClick={() => { setExpanded2(!expanded2); }}
                aria-expanded={expanded2}
              >
                <Text size="sm" weight="semibold">Deductibles</Text>
                <Icon icon={expanded2 ? ChevronUp : ChevronDown} size="sm" />
              </button>
              {expanded2 && (
                <div className="px-4 pb-4">
                  <VStack gap={2}>
                    <HStack justify="between">
                      <Text size="sm" color="muted">Collision</Text>
                      <Text size="sm">$500</Text>
                    </HStack>
                    <HStack justify="between">
                      <Text size="sm" color="muted">Comprehensive</Text>
                      <Text size="sm">$250</Text>
                    </HStack>
                  </VStack>
                </div>
              )}
            </Panel>
          </VStack>
      </ShowcaseDemoSection>

      {/* With Header Actions */}
      <ShowcaseDemoSection
        title="With Header Actions"
        description="Panels can include custom header layouts with action buttons"
       
      >
          <VStack gap={4}>
            <Panel className="max-w-md" padding="none">
              <HStack justify="between" align="center" className="p-4 border-b border-[#E8E6E3]">
                <Text size="sm" weight="semibold">Filter Options</Text>
                <Button variant="ghost" size="sm" leftIcon={<Icon icon={Filter} size="sm" />}>
                  Reset
                </Button>
              </HStack>
              <div className="p-4">
                <VStack gap={3}>
                  <div className="h-8 bg-[#E8E6E3] rounded w-full" />
                  <div className="h-8 bg-[#E8E6E3] rounded w-full" />
                  <div className="h-8 bg-[#E8E6E3] rounded w-full" />
                </VStack>
              </div>
            </Panel>

            <Panel className="max-w-md" padding="none">
              <HStack justify="between" align="center" className="p-4 border-b border-[#E8E6E3]">
                <Text size="sm" weight="semibold">Account Settings</Text>
                <Button variant="ghost" size="sm" leftIcon={<Icon icon={Settings} size="sm" />} aria-label="Settings" />
              </HStack>
              <div className="p-4">
                <VStack gap={2}>
                  <HStack justify="between">
                    <Text size="sm">Email notifications</Text>
                    <Text size="sm" color="muted">Enabled</Text>
                  </HStack>
                  <HStack justify="between">
                    <Text size="sm">SMS alerts</Text>
                    <Text size="sm" color="muted">Disabled</Text>
                  </HStack>
                  <HStack justify="between">
                    <Text size="sm">Auto-renewal</Text>
                    <Text size="sm" color="muted">Enabled</Text>
                  </HStack>
                </VStack>
              </div>
            </Panel>
          </VStack>
      </ShowcaseDemoSection>

      {/* Semantic Elements */}
      <ShowcaseDemoSection
        title="Semantic Elements"
        description="Panels can render as different HTML elements using the 'as' prop"
       
      >
          <HStack gap={4} wrap="wrap" align="start">
            <Panel as="section" className="w-48">
              <Text size="xs" weight="semibold" color="muted">as=&quot;section&quot;</Text>
              <Text size="sm" className="mt-1">Main content area</Text>
            </Panel>
            <Panel as="aside" variant="outlined" className="w-48">
              <Text size="xs" weight="semibold" color="muted">as=&quot;aside&quot;</Text>
              <Text size="sm" className="mt-1">Sidebar content</Text>
            </Panel>
            <Panel as="nav" variant="elevated" className="w-48">
              <Text size="xs" weight="semibold" color="muted">as=&quot;nav&quot;</Text>
              <Text size="sm" className="mt-1">Navigation area</Text>
            </Panel>
          </HStack>
      </ShowcaseDemoSection>

      {/* Panel vs Card Comparison */}
      <ShowcaseDemoSection
        title="Panel vs Card Comparison"
        description="When to use Panel vs Card"
       
      >
          <Grid columns={{ base: 1, md: 2 }} gap={4}>
            <VStack gap={2}>
              <Text size="sm" weight="semibold" color="muted">Panel (Simple)</Text>
              <Panel>
                <Text size="sm">
                  Use when you need a simple container without header/footer structure.
                  Good for sidebar widgets, filter groups, or inline content.
                </Text>
              </Panel>
            </VStack>
            <VStack gap={2}>
              <Text size="sm" weight="semibold" color="muted">Card (Structured)</Text>
              <Card>
                <CardHeader title="Card Title" subtitle="With subtitle" />
                <CardContent>
                  <Text size="sm">
                    Use when content needs clear header/footer sections, collapsible
                    behavior, or action areas.
                  </Text>
                </CardContent>
              </Card>
            </VStack>
          </Grid>
      </ShowcaseDemoSection>
    </VStack>
  );
}

export const PanelShowcase: ShowcaseDefinition = {
  id: 'panel',
  name: 'Panel',
  category: 'layout',
  description: 'Sectioned content area with optional collapsible behavior',
  propsDefinition: [
    {
      name: 'variant',
      type: 'select',
      options: ['default', 'elevated', 'outlined', 'ghost'],
      defaultValue: 'default',
      description: 'Visual style of the panel',
    },
    {
      name: 'padding',
      type: 'select',
      options: ['none', 'compact', 'default', 'comfortable'],
      defaultValue: 'default',
      description: 'Internal padding based on density mode',
    },
  ],
  accessibility: {
    ariaAttributes: [
      'Uses semantic element via as prop (div, section, aside, nav)',
      'aria-expanded on collapsible triggers',
    ],
    keyboardNav: [
      'Enter/Space: Toggle collapsible panels',
      'Tab: Navigate to focusable elements',
    ],
    notes: [
      'Use semantic elements (section, aside) for proper document structure',
      'Collapsible triggers should be buttons for keyboard accessibility',
      'Ensure proper heading hierarchy within panels',
    ],
  },
  component: PanelShowcaseComponent,
};
